import { useRef, useState } from 'react';

import { Truncate } from 'cpool-components/general/Truncate';
import { TextInput } from 'cpool-components/input/TextInput';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { LuPencil } from 'react-icons/lu';

import { useRefreshRecordedDocuments } from '../useRefreshRecordedDocuments';
import { APICommunicator } from '~/api/APICommunicator';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import useCallbackWithErrorHandler from '~/hooks/useCallbackWithErrorHandler';

type Props = {
    documentKey: string;
    fileName: string;
    leadId: string;
};

export const FileNameCell: React.FC<Props> = ({ documentKey, fileName, leadId }) => {
    const { t } = useTranslation();
    const refreshRecordedDocuments = useRefreshRecordedDocuments(leadId);
    // null = not editing
    const [draft, setDraft] = useState<string | null>(null);
    // Shown until the refetch delivers the new name, so the old one does not flash back in between.
    const [pendingName, setPendingName] = useState<string | null>(null);
    // Escape blurs explicitly; the flag keeps that blur from committing the draft.
    const isCancelled = useRef(false);
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const isDisabled = !permissions.commands[AGGREGATE_NAMES.LOAN_REQUEST].renameDocument;

    const onBlur = useCallbackWithErrorHandler(async () => {
        const nextName = draft;
        setDraft(null);

        if (isCancelled.current) {
            isCancelled.current = false;
            return;
        }
        if (!nextName || nextName === fileName) return;

        setPendingName(nextName);
        try {
            const { position } = await APICommunicator.sendCommand({
                aggregateId: leadId,
                aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                commandName: LOAN_REQUEST_COMMANDS.renameDocument,
                payload: { key: documentKey, name: nextName },
            });
            await refreshRecordedDocuments(position);
        } finally {
            setPendingName(null);
        }
    }, [documentKey, draft, fileName, leadId, refreshRecordedDocuments]);

    if (draft !== null)
        return (
            <TextInput
                id={`rename-${documentKey}`}
                aria-label={t('DOCUMENT_DEMANDER.SUMMARY.RENAME_FILE')}
                autoFocus
                className="w-full"
                inputClassName="px-2"
                onBlur={onBlur}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') event.currentTarget.blur();
                    if (event.key === 'Escape') {
                        isCancelled.current = true;
                        event.currentTarget.blur();
                    }
                }}
                value={draft}
            />
        );

    return (
        <div className="flex items-center gap-2">
            <Truncate text={pendingName ?? fileName} maxWidth={140} />
            <button
                aria-label={t('DOCUMENT_DEMANDER.SUMMARY.RENAME_FILE')}
                className="shrink-0 text-midnight disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isDisabled || pendingName !== null}
                onClick={() => setDraft(fileName)}
                type="button"
            >
                <LuPencil size={14} />
            </button>
        </div>
    );
};
